import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Pencil, Sparkles, Loader2, Unlock, Drama, Shield, Save, X } from 'lucide-react';
import { ratingError } from '@/lib/owaspRating';
import RiskRatingField from './RiskRatingField';
import SeverityBadge from './SeverityBadge';
import ReviewCommentsCallout, { type ReviewCommentWithReplies } from '@/components/review/ReviewCommentsCallout';
import { threatStatusColors } from '@/lib/constants';
import type { Threat, ThreatStatus } from '@/types/threat';

export interface ThreatCardProps {
  threat: Threat;
  index: number;
  diagramName?: string;
  externalId?: string | null;
  canEdit: boolean;
  onSave: (index: number, updated: Threat) => void;
  onEnrichWithAI: (threat: Threat) => Promise<Record<string, string>>;
  isEnriching: boolean;
  isNew?: boolean;
  onAdd?: (threat: Threat) => void;
  onCancelAdd?: () => void;
  /** Restricts the selectable statuses (e.g. ThreatCat-backed threats only
   * support ThreatCat's own ThreatStatusOptions enum). Defaults to the full
   * local lifecycle. */
  allowedStatuses?: ThreatStatus[];
  editDisabledReason?: string | null;
  reviewComments?: ReviewCommentWithReplies[];
}

const THREAT_STATUSES: ThreatStatus[] = ['open', 'mitigated', 'invalid', 'in-progress', 'closed'];

export default function ThreatCard({
  threat,
  index,
  diagramName,
  externalId,
  canEdit,
  onSave,
  onEnrichWithAI,
  isEnriching,
  isNew = false,
  onAdd,
  onCancelAdd,
  allowedStatuses = THREAT_STATUSES,
  editDisabledReason = null,
  reviewComments = [],
}: ThreatCardProps) {
  const [editMode, setEditMode] = useState(isNew);
  const [draftName, setDraftName] = useState('');
  const [draftVulnerability, setDraftVulnerability] = useState('');
  const [draftScenario, setDraftScenario] = useState('');
  const [draftCountermeasure, setDraftCountermeasure] = useState('');
  const [draftRating, setDraftRating] = useState('');
  const [draftStatus, setDraftStatus] = useState<ThreatStatus>('open');
  const [draftStatusNote, setDraftStatusNote] = useState('');
  const [enrichError, setEnrichError] = useState<string | null>(null);

  const handleStartEdit = () => {
    if (editDisabledReason) return;
    setDraftName(threat.name);
    setDraftVulnerability(threat.vulnerability ?? '');
    setDraftScenario(threat.scenario ?? '');
    setDraftCountermeasure(threat.countermeasure ?? '');
    setDraftRating(threat.rating_vector_string ?? '');
    setDraftStatus(threat.status);
    setDraftStatusNote(threat.status_note ?? '');
    setEnrichError(null);
    setEditMode(true);
  };

  const handleCancelNew = () => {
    onCancelAdd?.();
  };

  const handleCancelEdit = () => {
    if (isNew) {
      handleCancelNew();
      return;
    }
    setEditMode(false);
    setEnrichError(null);
  };

  const handleSaveEdit = () => {
    if (!isNew && editDisabledReason) return;
    const updated: Threat = {
      ...threat,
      name: draftName,
      vulnerability: draftVulnerability,
      scenario: draftScenario,
      countermeasure: draftCountermeasure,
      rating_vector_string: draftRating.trim() || null,
      status: draftStatus,
      status_note: (draftStatus === 'mitigated' || draftStatus === 'invalid')
        ? draftStatusNote
        : undefined,
    };
    if (isNew) {
      onAdd?.(updated);
    } else {
      onSave(index, updated);
      setEditMode(false);
    }
  };

  const handleEnrich = async () => {
    setEnrichError(null);
    try {
      const draftThreat: Threat = {
        ...threat,
        name: draftName,
        vulnerability: draftVulnerability,
        scenario: draftScenario,
        countermeasure: draftCountermeasure,
        rating_vector_string: draftRating.trim() || null,
        status: draftStatus,
      };
      const result = await onEnrichWithAI(draftThreat);
      if (result.vulnerability !== undefined) setDraftVulnerability(result.vulnerability);
      if (result.scenario !== undefined) setDraftScenario(result.scenario);
      if (result.countermeasure !== undefined) setDraftCountermeasure(result.countermeasure);
      if (result.rating_vector_string !== undefined) setDraftRating(result.rating_vector_string);
    } catch (err) {
      setEnrichError(err instanceof Error ? err.message : 'AI enrichment failed');
    }
  };

  const reviewCallout = <ReviewCommentsCallout comments={reviewComments} />;

  if (editMode) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <CardTitle className="text-lg">{isNew ? 'New Threat' : 'Edit Threat'}</CardTitle>
            <div className="flex items-center gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button size="sm" variant="outline" onClick={handleEnrich} disabled={isEnriching}>
                    {isEnriching ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Sparkles className="h-4 w-4 mr-1" />}
                    Generate with AI
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Generate vulnerability, scenario, countermeasure and risk rating using AI</TooltipContent>
              </Tooltip>
              <Button
                size="sm"
                onClick={handleSaveEdit}
                disabled={!draftName.trim() || !!ratingError(draftRating) || (!isNew && Boolean(editDisabledReason))}
                title={!isNew && editDisabledReason ? editDisabledReason : undefined}
              >
                <Save className="h-4 w-4 mr-1" />Save
              </Button>
              <Button size="sm" variant="ghost" onClick={handleCancelEdit}>
                <X className="h-4 w-4 mr-1" />Cancel
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {reviewCallout}
          {enrichError && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{enrichError}</div>
          )}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Name</label>
              <Input value={draftName} onChange={(e) => setDraftName(e.target.value)} />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Status</label>
              <Select value={draftStatus} onValueChange={(v) => setDraftStatus(v as ThreatStatus)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {allowedStatuses.map((s) => (
                    <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Vulnerability</label>
            <Textarea value={draftVulnerability} onChange={(e) => setDraftVulnerability(e.target.value)} rows={3} />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Threat Scenario</label>
            <Textarea value={draftScenario} onChange={(e) => setDraftScenario(e.target.value)} rows={3} />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Countermeasure</label>
            <Textarea value={draftCountermeasure} onChange={(e) => setDraftCountermeasure(e.target.value)} rows={3} />
          </div>
          <RiskRatingField value={draftRating} onChange={setDraftRating} threatType={threat.type} />
          {(draftStatus === 'mitigated' || draftStatus === 'invalid') && (
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Status Note</label>
              <Textarea
                value={draftStatusNote}
                onChange={(e) => setDraftStatusNote(e.target.value)}
                rows={2}
                placeholder={`Why is this threat ${draftStatus}?`}
              />
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2">
              {externalId && (
                <span className="font-mono text-xs text-muted-foreground bg-muted rounded px-1.5 py-0.5 shrink-0">{externalId}</span>
              )}
              <CardTitle className="text-lg">{threat.name}</CardTitle>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${
                threatStatusColors[threat.status] || 'bg-gray-100 text-gray-800'
              }`}>
                {threat.status.charAt(0).toUpperCase() + threat.status.slice(1)}
              </span>
              {diagramName && (
                <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10 dark:bg-blue-900/30 dark:text-blue-400">
                  {diagramName}
                </span>
              )}
              {threat.type && (
                <span className="inline-flex items-center rounded-md bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700 ring-1 ring-inset ring-purple-700/10 dark:bg-purple-900/30 dark:text-purple-400">
                  {threat.type}
                </span>
              )}
              <SeverityBadge vector={threat.rating_vector_string} threatType={threat.type} />
            </div>
          </div>
          {canEdit && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span title={editDisabledReason ?? undefined} className="inline-flex shrink-0">
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label="Edit threat"
                    title={editDisabledReason ?? 'Edit threat'}
                    onClick={handleStartEdit}
                    disabled={Boolean(editDisabledReason)}
                    className="shrink-0"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>{editDisabledReason ?? 'Edit threat'}</TooltipContent>
            </Tooltip>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {reviewCallout}
        {threat.vulnerability && (
          <div className="space-y-2 rounded-lg border bg-muted/50 p-4">
            <div className="text-sm font-semibold flex items-center gap-1.5"><Unlock className="h-4 w-4" />Vulnerability</div>
            <p className="text-sm leading-relaxed text-muted-foreground whitespace-pre-wrap">
              {threat.vulnerability}
            </p>
          </div>
        )}
        {threat.scenario && (
          <div className="space-y-2 rounded-lg border bg-muted/50 p-4">
            <div className="text-sm font-semibold flex items-center gap-1.5"><Drama className="h-4 w-4" />Threat Scenario</div>
            <p className="text-sm leading-relaxed text-muted-foreground whitespace-pre-wrap">
              {threat.scenario}
            </p>
          </div>
        )}
        {threat.countermeasure && (
          <div className="space-y-2 rounded-lg border bg-muted/50 p-4">
            <div className="text-sm font-semibold flex items-center gap-1.5"><Shield className="h-4 w-4" />Countermeasure</div>
            <p className="text-sm leading-relaxed text-muted-foreground whitespace-pre-wrap">
              {threat.countermeasure}
            </p>
          </div>
        )}
        {threat.status_note && (
          <div className="space-y-2 rounded-lg border bg-muted/50 p-4">
            <div className="text-sm font-semibold flex items-center gap-1.5">Status Note</div>
            <p className="text-sm leading-relaxed text-muted-foreground whitespace-pre-wrap">
              {threat.status_note}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
